import React, { useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import * as XLSX from "xlsx-js-style";
import { GenericsProductItems } from "./pages/GenericsProductItems";
import { ComplexGenericsItems } from "./pages/ComplexGenericsItems";
import { DifferntiatedProdDevItems } from "./pages/DifferntiatedProdDevItems";
import { InnovativeProdDevItems } from "./pages/InnovativeProdDevItems";

const ContactUsCard = (props) => {
  const location = useLocation();
  const [isMinimized, setIsMinimized] = useState(true);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const positionRef = useRef({ x: 0, y: 0 });

  const [headLine, setHeadLine] = useState(null);
  const [showDownloadDropdown, setShowDownloadDropdown] = useState(false);

  const handleDownloadClick = (type) => {
    setShowDownloadDropdown(false);
    try {
      let portfolios = [];
      if (type === "all" || type === "generics") {
        portfolios.push({ name: "Generics", data: GenericsProductItems, type: "Standard" });
      }
      if (type === "all" || type === "complex") {
        portfolios.push({ name: "Complex Generics", data: ComplexGenericsItems, type: "Standard" });
      }
      if (type === "all" || type === "differentiated") {
        portfolios.push({ name: "Differentiated", data: DifferntiatedProdDevItems, type: "Standard" });
      }
      if (type === "all" || type === "innovative") {
        portfolios.push({ name: "Innovative", data: InnovativeProdDevItems, type: "Innovative" });
      }

      const wb = XLSX.utils.book_new();

      portfolios.forEach((portfolio) => {
        const formattedData = portfolio.data.map((item, idx) => {
          const row = {
            "S.No.": idx + 1,
            "Product Name": item.productName || "",
            "RLD / Reference Brand": item.rLDProduct_Dev || "",
          };

          if (portfolio.type === "Innovative") {
            row["POC / Feasibility"] = item.productDevStatus || "";
            row["IP Status (Filed/POC)"] = item.ipStatus || "";
            row["Lab Scale Up"] = item.labStability || "";
            row["In-Vivo Studies"] = item.poc || "";
            row["Licensing / Tech Transfer"] = item.licensing || "";
          } else {
            row["Development Status"] = item.productDevStatus || "";
            row["Pilot BE Status"] = item.pilotBE || "";
            row["Scale Up Status"] = item.scaleUp || "";
            row["Pivotal BE Status"] = item.pivotalBE || "";
            row["Tech Transfer Status"] = item.technologyTransfer || "";
          }

          const getRegionAvailability = (regions, key) => {
            if (!regions) return "";
            const reg = regions.find(r => r.key === key);
            return reg && reg.value ? "Available" : "";
          };

          row["USA Availability"] = getRegionAvailability(item.avaliableIn, "USA");
          row["EU Availability"] = getRegionAvailability(item.avaliableIn, "EU");
          row["China Availability"] = getRegionAvailability(item.avaliableIn, "China");
          row["Australia Availability"] = getRegionAvailability(item.avaliableIn, "Australia");
          row["S.E. Asia Availability"] = getRegionAvailability(item.avaliableIn, "S.E. Asia");
          row["Other Regions Availability"] = getRegionAvailability(item.avaliableIn, "Other");

          row["Remarks / Regulatory Filings"] = item.remark || "";
          return row;
        });

        // 3. Create professional security headers (Confidential & Proprietary)
        const headerInfo = [
          [`DELNOVA HEALTHCARE - ${portfolio.name.toUpperCase()} PORTFOLIO`],
          ["CONFIDENTIALITY WARNING: This document contains proprietary information and trade secrets of DelNova Healthcare. Unauthorized disclosure, copying or distribution is strictly prohibited."],
          [] // Empty separator row
        ];

        const ws = XLSX.utils.aoa_to_sheet(headerInfo);
        XLSX.utils.sheet_add_json(ws, formattedData, { origin: "A4" });

        const range = XLSX.utils.decode_range(ws["!ref"]);

        // Apply visual styling to cells
        for (let R = range.s.r; R <= range.e.r; ++R) {
          for (let C = range.s.c; C <= range.e.c; ++C) {
            const cell_ref = XLSX.utils.encode_cell({ r: R, c: C });
            if (!ws[cell_ref]) continue;
            const cell = ws[cell_ref];

            if (R === 0) {
              // Title Banner: Dark brand-blue, white bold text
              cell.s = {
                fill: { patternType: "solid", fgColor: { rgb: "1E3A8A" } },
                font: { name: "Arial", sz: 12, bold: true, color: { rgb: "FFFFFF" } },
                alignment: { horizontal: "center", vertical: "center" }
              };
            } else if (R === 1) {
              // Confidential Banner: Soft red background, red italic bold warning
              cell.s = {
                fill: { patternType: "solid", fgColor: { rgb: "FEF2F2" } },
                font: { name: "Arial", sz: 8.5, italic: true, bold: true, color: { rgb: "991B1B" } },
                alignment: { horizontal: "center", vertical: "center", wrapText: true }
              };
            } else if (R === 3) {
              // Column Headers: Teal, white bold text, thick borders
              cell.s = {
                fill: { patternType: "solid", fgColor: { rgb: "0F766E" } },
                font: { name: "Arial", sz: 10, bold: true, color: { rgb: "FFFFFF" } },
                alignment: { horizontal: "center", vertical: "center", wrapText: true },
                border: {
                  top: { style: "thin", color: { rgb: "475569" } },
                  bottom: { style: "medium", color: { rgb: "1E293B" } },
                  left: { style: "thin", color: { rgb: "475569" } },
                  right: { style: "thin", color: { rgb: "475569" } }
                }
              };
            } else if (R >= 4) {
              // Data Cells: Alternate zebra fill, thin borders, alignments
              const isEven = (R % 2 === 0);
              const isCenterCol = (C === 0 || (C >= 3 && C <= 13));
              cell.s = {
                fill: { patternType: "solid", fgColor: { rgb: isEven ? "F8FAFC" : "FFFFFF" } },
                font: { name: "Arial", sz: 9.5, color: { rgb: "334155" } },
                alignment: { 
                  horizontal: isCenterCol ? "center" : "left", 
                  vertical: "center",
                  wrapText: true 
                },
                border: {
                  top: { style: "thin", color: { rgb: "E2E8F0" } },
                  bottom: { style: "thin", color: { rgb: "E2E8F0" } },
                  left: { style: "thin", color: { rgb: "E2E8F0" } },
                  right: { style: "thin", color: { rgb: "E2E8F0" } }
                }
              };
            }
          }
        }

        // Merge headers
        ws["!merges"] = [
          { s: { r: 0, c: 0 }, e: { r: 0, c: 14 } },
          { s: { r: 1, c: 0 }, e: { r: 1, c: 14 } }
        ];

        // Autofit column widths dynamically
        const colWidths = [];
        for (let C = range.s.c; C <= range.e.c; ++C) {
          let maxLen = 10;
          for (let R = 3; R <= range.e.r; ++R) {
            const cell_ref = XLSX.utils.encode_cell({ r: R, c: C });
            if (ws[cell_ref] && ws[cell_ref].v) {
              const valStr = String(ws[cell_ref].v);
              if (valStr.length > maxLen) {
                maxLen = valStr.length;
              }
            }
          }
          colWidths[C] = { wch: Math.min(maxLen + 4, 45) };
        }
        ws["!cols"] = colWidths;

        // Custom row heights
        const rowHeights = [];
        rowHeights[0] = { hpt: 30 };
        rowHeights[1] = { hpt: 25 };
        rowHeights[2] = { hpt: 15 };
        rowHeights[3] = { hpt: 28 };
        for (let R = 4; R <= range.e.r; ++R) {
          rowHeights[R] = { hpt: 22 };
        }
        ws["!rows"] = rowHeights;

        // Freeze top 4 rows
        ws["!views"] = [
          {
            state: "frozen",
            xSplit: 0,
            ySplit: 4,
            topLeftCell: "A5",
            activePane: "bottomLeft"
          }
        ];

        // Apply read-only sheet protection
        ws["!protect"] = {
          selectLockedCells: true,
          selectUnlockedCells: true
        };

        XLSX.utils.book_append_sheet(wb, ws, portfolio.name);
      });

      const filename = type === "all" 
        ? "DelNova_Full_Product_Portfolio.xlsx" 
        : `DelNova_${type.charAt(0).toUpperCase() + type.slice(1)}_List.xlsx`;
      
      XLSX.writeFile(wb, filename);
    } catch (error) {
      console.error("Failed to generate Excel portfolio sheet:", error);
    }
  };

  // If we are on pages that shouldn't show it (like the CDMO Pipeline page)
  if (location.pathname === "/Pipeline") {
    return null;
  }

  const HeadLineSelector = () => {
    if (props.sender === "Diff" || props.sender === "Inno") {
      setHeadLine("Share Your Idea Here");
    } else {
      setHeadLine("Share Your Generic Product Idea ");
    }
  };

  const handlePointerDown = (e) => {
    // Only drag with left click or touch pointer
    if (e.button !== undefined && e.button !== 0) return;

    // Do not drag if clicking active buttons or anchors to allow clicking normal actions
    if (
      e.target.tagName === "BUTTON" ||
      e.target.tagName === "A" ||
      e.target.closest("button") ||
      e.target.closest("a")
    ) {
      return;
    }

    setIsDragging(true);
    dragStartRef.current = { x: e.clientX, y: e.clientY };
    positionRef.current = { ...position };
    e.target.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e) => {
    if (!isDragging) return;
    const dx = e.clientX - dragStartRef.current.x;
    const dy = e.clientY - dragStartRef.current.y;
    setPosition({
      x: positionRef.current.x + dx,
      y: positionRef.current.y + dy,
    });
  };

  const handlePointerUp = (e) => {
    if (!isDragging) return;
    setIsDragging(false);
    e.target.releasePointerCapture(e.pointerId);
  };

  const questionText =
    props.sender === "Diff" || props.sender === "Inno"
      ? "Do you have any idea?"
      : "Do you want to develop any new generic product?";

  // Render navigation links based on current page context
  const renderRelatedLinks = () => {
    // Styling class for the elegant outline list links
    const linkClass = "flex items-center justify-between w-full bg-slate-50/50 hover:bg-brand-blue/5 border border-slate-200 hover:border-brand-blue/30 text-slate-700 hover:text-brand-blue font-semibold text-xs py-2 px-3.5 rounded-xl transition-all duration-200 group";

    switch (props.sender) {
      case "Diff":
        return (
          <>
            <Link to="/InnovativeProdDev" className={linkClass}>
              <span>Innovative</span>
              <svg className="w-3 h-3 text-slate-400 group-hover:text-brand-blue transform group-hover:translate-x-0.5 transition-all" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" />
              </svg>
            </Link>
            <Link to="/ComplexGenerics" className={linkClass}>
              <span>Complex</span>
              <svg className="w-3 h-3 text-slate-400 group-hover:text-brand-blue transform group-hover:translate-x-0.5 transition-all" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" />
              </svg>
            </Link>
            <Link to="/GenericsProduct" className={linkClass}>
              <span>Generic</span>
              <svg className="w-3 h-3 text-slate-400 group-hover:text-brand-blue transform group-hover:translate-x-0.5 transition-all" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </>
        );
      case "Inno":
        return (
          <>
            <Link to="/DifferntiatedProdDev" className={linkClass}>
              <span>Differentiated</span>
              <svg className="w-3 h-3 text-slate-400 group-hover:text-brand-blue transform group-hover:translate-x-0.5 transition-all" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" />
              </svg>
            </Link>
            <Link to="/ComplexGenerics" className={linkClass}>
              <span>Complex</span>
              <svg className="w-3 h-3 text-slate-400 group-hover:text-brand-blue transform group-hover:translate-x-0.5 transition-all" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" />
              </svg>
            </Link>
            <Link to="/GenericsProduct" className={linkClass}>
              <span>Generic</span>
              <svg className="w-3 h-3 text-slate-400 group-hover:text-brand-blue transform group-hover:translate-x-0.5 transition-all" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </>
        );
      case "Comp":
        return (
          <>
            <Link to="/DifferntiatedProdDev" className={linkClass}>
              <span>Differentiated</span>
              <svg className="w-3 h-3 text-slate-400 group-hover:text-brand-blue transform group-hover:translate-x-0.5 transition-all" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" />
              </svg>
            </Link>
            <Link to="/InnovativeProdDev" className={linkClass}>
              <span>Innovative</span>
              <svg className="w-3 h-3 text-slate-400 group-hover:text-brand-blue transform group-hover:translate-x-0.5 transition-all" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" />
              </svg>
            </Link>
            <Link to="/GenericsProduct" className={linkClass}>
              <span>Generic</span>
              <svg className="w-3 h-3 text-slate-400 group-hover:text-brand-blue transform group-hover:translate-x-0.5 transition-all" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </>
        );
      case "Genr":
      default:
        return (
          <>
            <Link to="/DifferntiatedProdDev" className={linkClass}>
              <span>Differentiated</span>
              <svg className="w-3 h-3 text-slate-400 group-hover:text-brand-blue transform group-hover:translate-x-0.5 transition-all" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" />
              </svg>
            </Link>
            <Link to="/InnovativeProdDev" className={linkClass}>
              <span>Innovative</span>
              <svg className="w-3 h-3 text-slate-400 group-hover:text-brand-blue transform group-hover:translate-x-0.5 transition-all" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" />
              </svg>
            </Link>
            <Link to="/ComplexGenerics" className={linkClass}>
              <span>Complex</span>
              <svg className="w-3 h-3 text-slate-400 group-hover:text-brand-blue transform group-hover:translate-x-0.5 transition-all" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </>
        );
    }
  };

  const baseStyle = {
    position: "fixed",
    bottom: "2.5rem",
    right: "2.5rem",
    transform: `translate(${position.x}px, ${position.y}px)`,
    cursor: isDragging ? "grabbing" : "grab",
    zIndex: 50,
    touchAction: "none",
  };

  return (
    <div
      style={baseStyle}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      className="font-sans select-none"
    >
      {isMinimized ? (
        /* Minimized State (FAB Badge) */
        <button
          onClick={() => setIsMinimized(false)}
          className="flex items-center space-x-2.5 bg-gradient-to-r from-brand-blue to-brand-teal hover:from-brand-blue-light hover:to-brand-teal-light text-white px-5 py-3 rounded-full shadow-2xl border border-white/20 transition-all duration-300 transform hover:scale-[1.03] active:scale-[0.97] group focus:outline-none"
        >
          <div className="relative flex-shrink-0">
            <div className="absolute -inset-1 rounded-full bg-brand-teal/40 animate-ping group-hover:bg-brand-teal-light/50" />
            <svg
              className="w-5 h-5 text-white relative z-10"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2.2"
                d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"
              />
            </svg>
          </div>
          <span className="font-bold text-xs tracking-wider uppercase">
            Innovation Desk
          </span>
          <svg
            className="w-3.5 h-3.5 text-white/70 group-hover:translate-x-0.5 transition-transform"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M9 5l7 7-7 7"
            />
          </svg>
        </button>
      ) : (
        /* Expanded Dashboard Card */
        <div className="w-[310px] bg-white/95 backdrop-blur-md border border-slate-200/50 rounded-3xl p-6 shadow-2xl transition-all duration-300 transform scale-100 flex flex-col relative select-none">
          {/* Drag Handle Indicator */}
          <div className="w-10 h-1 bg-slate-200 rounded-full mx-auto mb-4 cursor-grab" />

          {/* Close / Minimize Button */}
          <button
            onClick={() => setIsMinimized(true)}
            className="absolute top-5 right-5 p-1.5 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-400 hover:text-slate-600 transition-colors focus:outline-none"
            title="Minimize"
          >
            <svg
              className="w-3.5 h-3.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2.5"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>

          {/* Body Content */}
          <div className="space-y-5">
            {/* Title / Question */}
            <div className="pr-6 text-left">
              <h4 className="text-[13px] font-bold text-brand-blue leading-normal tracking-wide uppercase">
                {questionText}
              </h4>
            </div>

            {/* Primary Action Button (Contact Us) */}
            <Link
              to="/ShareYourIdea"
              state={{ from: headLine }}
              onMouseDown={HeadLineSelector}
              className="flex items-center justify-center space-x-2 bg-gradient-to-r from-brand-teal to-brand-teal-dark hover:from-brand-teal-light hover:to-brand-teal text-white font-bold text-xs uppercase tracking-wider py-2.5 px-4 rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-center"
            >
              <span>Contact Us</span>
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2.5"
                  d="M14 5l7 7m0 0l-7 7m7-7H3"
                />
              </svg>
            </Link>

            {/* Portfolio Links Grid */}
            <div className="space-y-2">
              <span className="block text-[9px] font-bold text-slate-400 uppercase tracking-widest text-left">
                Related Portfolios
              </span>
              <div className="grid grid-cols-1 gap-2">
                {renderRelatedLinks()}
              </div>
            </div>

            {/* Product List Download */}
            <div className="pt-4 border-t border-slate-100 flex flex-col space-y-2 text-left relative">
              <span className="text-[9px] text-slate-400 font-bold uppercase tracking-widest">
                Complete Products List
              </span>
              <button
                onClick={() => setShowDownloadDropdown(!showDownloadDropdown)}
                className="w-full flex items-center justify-center space-x-2 bg-brand-teal/10 hover:bg-brand-teal/20 text-brand-teal-dark font-bold text-xs py-2.5 px-4 rounded-xl transition-all duration-200"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2.2"
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                  />
                </svg>
                <span>Download Portfolio</span>
                <svg
                  className={`w-3.5 h-3.5 ml-1 transition-transform duration-200 ${showDownloadDropdown ? "transform rotate-180" : ""}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Dropdown Options Popover */}
              {showDownloadDropdown && (
                <div className="absolute left-0 right-0 bottom-full mb-2 bg-white border border-slate-100 rounded-2xl shadow-xl z-50 p-2 space-y-0.5 animate-fadeIn">
                  <div className="px-3 py-1.5 border-b border-slate-50">
                    <span className="block text-[8px] font-bold text-slate-400 uppercase tracking-wider">
                      Select Portfolio Category
                    </span>
                  </div>
                  {[
                    { id: "all", label: "All Portfolios (Multi-sheet)" },
                    { id: "generics", label: "Generics Only" },
                    { id: "complex", label: "Complex Generics" },
                    { id: "differentiated", label: "Differentiated Formulations" },
                    { id: "innovative", label: "Innovative Products" },
                  ].map((option) => (
                    <button
                      key={option.id}
                      onClick={() => handleDownloadClick(option.id)}
                      className="w-full text-left px-3 py-2 text-xs font-semibold text-slate-600 hover:text-brand-blue hover:bg-slate-50 rounded-lg transition-colors flex items-center justify-between"
                    >
                      <span>{option.label}</span>
                      <svg className="w-3 h-3 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactUsCard;
